import {
  ATTACK_ASSET_KEYS,
  BATTLE_ASSET_KEYS,
  BATTLE_BACKGROUND_ASSET_KEYS,
  CHARACTER_ASSET_KEYS,
  EXTERNAL_LINKS_ASSET_KEYS,
  DATA_ASSET_KEYS,
  HEALTH_BAR_ASSET_KEYS,
  INVENTORY_ASSET_KEYS,
  MONSTER_ASSET_KEYS,
  MONSTER_PARTY_ASSET_KEYS,
  PLAYER_SKIN_KEYS,
  TITLE_ASSET_KEYS,
  UI_ASSET_KEYS,
  WORLD_ASSET_KEYS,
  AUDIO_ASSET_KEYS,
  BUILDING_ASSET_KEYS,
  EXP_BAR_ASSET_KEYS,
  LOWPOLY_ASSET_KEYS,
} from '../assets/asset-keys.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { WebFontFileLoader } from '../assets/web-font-file-loader.js';
import { DataUtils } from '../utils/data-utils.js';
import { dataManager } from '../utils/data-manager.js';
import { SHOW_SOCIAL_LINKS } from '../config.js';
import { BaseScene } from './base-scene.js';
import { setGlobalSoundSettings } from '../utils/audio-utils.js';

export class PreloadScene extends BaseScene {
  constructor() {
    super({
      key: SCENE_KEYS.PRELOAD_SCENE,
    });
  }

  /**
   * @returns {void}
   */
  preload() {
    super.preload();

    const monsterTamerAssetPath = 'assets/images/monster-tamer';
    const kenneysAssetPath = 'assets/images/kenneys-assets';
    const pimenAssetPath = 'assets/images/pimen';
    const axulArtAssetPath = 'assets/images/axulart';
    const pbGamesAssetPath = 'assets/images/parabellum-games';
    const catchimonAssetPath = 'assets/images/catchimon';
    const mpwspAssetPath = 'assets/images/mpwsp';

    // external social links assets
    if (SHOW_SOCIAL_LINKS) {
      this.load.image(
        EXTERNAL_LINKS_ASSET_KEYS.GITHUB_BANNER,
        'assets/images/external-social/forkme_right_red_aa0000.webp'
      );
      this.load.image(
        EXTERNAL_LINKS_ASSET_KEYS.YOUTUBE_BUTTON,
        'assets/images/external-social/WatchonYouTube-white-3xPNG.png'
      );
      this.load.image(EXTERNAL_LINKS_ASSET_KEYS.LEARN_MORE_BACKGROUND, 'assets/images/external-social/blank.png');
      this.load.image(EXTERNAL_LINKS_ASSET_KEYS.YOUTUBE_THUMB_NAIL, 'assets/images/external-social/thumbnail.jpeg');
    }

    // battle backgrounds — the arena field (two stone platforms; one monster stands on each).
    this.load.image(
      BATTLE_BACKGROUND_ASSET_KEYS.FOREST,
      `${monsterTamerAssetPath}/battle-backgrounds/arena-background.png`
    );
    this.load.image(
      BATTLE_BACKGROUND_ASSET_KEYS.CAVE,
      `${monsterTamerAssetPath}/battle-backgrounds/cave-background.png`
    );
    this.load.image(
      BATTLE_BACKGROUND_ASSET_KEYS.TOWN,
      `${monsterTamerAssetPath}/battle-backgrounds/town-background.png`
    );

    // battle assets
    this.load.image(BATTLE_ASSET_KEYS.HEALTH_BAR_BACKGROUND, `${kenneysAssetPath}/ui-space-expansion/custom-ui.png`);
    this.load.image(BATTLE_ASSET_KEYS.BALL_THUMBNAIL, `${monsterTamerAssetPath}/battle/cosmoball.png`);
    this.load.image(BATTLE_ASSET_KEYS.DAMAGED_BALL, `${monsterTamerAssetPath}/battle/damagedBall.png`);
    this.load.image(BATTLE_ASSET_KEYS.TRAINER_YOUTH_BOY, `${monsterTamerAssetPath}/battle/trainer_youth_boy.png`);
    this.load.image(BATTLE_ASSET_KEYS.TRAINER_YOUTH_GIRL, `${monsterTamerAssetPath}/battle/trainer_youth_girl.png`);

    // health bar assets
    this.load.image(
      HEALTH_BAR_ASSET_KEYS.RIGHT_CAP,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_green_right.png`
    );
    this.load.image(HEALTH_BAR_ASSET_KEYS.MIDDLE, `${kenneysAssetPath}/ui-space-expansion/barHorizontal_green_mid.png`);
    this.load.image(
      HEALTH_BAR_ASSET_KEYS.LEFT_CAP,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_green_left.png`
    );

    // exp bar assets
    this.load.image(
      EXP_BAR_ASSET_KEYS.EXP_RIGHT_CAP,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_blue_right.png`
    );
    this.load.image(EXP_BAR_ASSET_KEYS.EXP_MIDDLE, `${kenneysAssetPath}/ui-space-expansion/barHorizontal_blue_mid.png`);
    this.load.image(
      EXP_BAR_ASSET_KEYS.EXP_LEFT_CAP,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_blue_left.png`
    );

    this.load.image(
      HEALTH_BAR_ASSET_KEYS.RIGHT_CAP_SHADOW,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_shadow_right.png`
    );
    this.load.image(
      HEALTH_BAR_ASSET_KEYS.MIDDLE_SHADOW,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_shadow_mid.png`
    );
    this.load.image(
      HEALTH_BAR_ASSET_KEYS.LEFT_CAP_SHADOW,
      `${kenneysAssetPath}/ui-space-expansion/barHorizontal_shadow_left.png`
    );

    // monster assets — beast keys are original (Monstara).
    // Legacy monster-tamer placeholders (256x256) still back stone/gale/venom/frost/beast/apex.
    this.load.image(MONSTER_ASSET_KEYS.GLOOMFANG, `${monsterTamerAssetPath}/monsters/carnodusk.png`);
    this.load.image(MONSTER_ASSET_KEYS.CINDERTAIL, `${monsterTamerAssetPath}/monsters/iguanignite.png`);
    this.load.image(MONSTER_ASSET_KEYS.TIDEMAW, `${monsterTamerAssetPath}/monsters/aquavalor.png`);
    this.load.image(MONSTER_ASSET_KEYS.RIMEHOWL, `${monsterTamerAssetPath}/monsters/frostsaber.png`);
    this.load.image(MONSTER_ASSET_KEYS.VOLTCRAG, `${monsterTamerAssetPath}/monsters/ignivolt.png`);
    // Real Catchimon battlers (80x80) — distinct creatures for flame / tide / verdant lines.
    // monsters.json carries a per-monster `assetScale` so these upscale to match the 256px sprites.
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_FIRE_01, `${catchimonAssetPath}/creatures/fire_01_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_FIRE_02, `${catchimonAssetPath}/creatures/fire_02_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_WATER_11, `${catchimonAssetPath}/creatures/water_11_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_WATER_12, `${catchimonAssetPath}/creatures/water_12_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_WATER_13, `${catchimonAssetPath}/creatures/water_13_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_GRASS_19, `${catchimonAssetPath}/creatures/grass_19_front.png`);
    this.load.image(MONSTER_ASSET_KEYS.CATCHIMON_GRASS_20, `${catchimonAssetPath}/creatures/grass_20_front.png`);
    // Ultra-rare deep-Cave beasts (custom art, bg removed). Per-monster assetScale normalizes magnitude.
    this.load.image(MONSTER_ASSET_KEYS.RARE_VOLTBLOOM, `${catchimonAssetPath}/creatures/rare_voltbloom.png`);
    this.load.image(MONSTER_ASSET_KEYS.RARE_NYXWING, `${catchimonAssetPath}/creatures/rare_nyxwing.png`);
    this.load.image(MONSTER_ASSET_KEYS.RARE_VOLTFOX, `${catchimonAssetPath}/creatures/rare_voltfox.png`);
    this.load.image(MONSTER_ASSET_KEYS.RARE_CRYOLITH, `${catchimonAssetPath}/creatures/rare_cryolith.png`);
    this.load.image(MONSTER_ASSET_KEYS.RARE_FERROK, `${catchimonAssetPath}/creatures/rare_ferrok.png`);

    // New Monstara creatures (80x80, transparent) — pixelArt rendering keeps them crisp at battle scale.
    const monstaraAssetPath = 'assets/images/monstara';
    this.load.image(MONSTER_ASSET_KEYS.NM_FIREFOX, `${monstaraAssetPath}/firefox.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_VOLTBEAST, `${monstaraAssetPath}/voltbeast.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_STEELGOLEM, `${monstaraAssetPath}/steelgolem.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_BATDRAGON, `${monstaraAssetPath}/batdragon.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_ICEDRAGON, `${monstaraAssetPath}/icedragon.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_CRYSTALDRAGON, `${monstaraAssetPath}/crystaldragon.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_CRYSTALFLYER, `${monstaraAssetPath}/crystalflyer.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_GREENDINO, `${monstaraAssetPath}/greendino.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_DOLPHIN, `${monstaraAssetPath}/dolphin.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_FINLIZARD, `${monstaraAssetPath}/finlizard.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_SOLANI, `${monstaraAssetPath}/solani.png`);
    this.load.image(MONSTER_ASSET_KEYS.NM_SOLANOID, `${monstaraAssetPath}/solanoid.png`);

    // low-poly RPG prop atlas (top-down decor + interactive objects)
    this.load.atlas(
      LOWPOLY_ASSET_KEYS.PROPS,
      'assets/images/lowpoly/props-top.png',
      'assets/images/lowpoly/props-top.json'
    );

    // ui assets
    this.load.image(UI_ASSET_KEYS.CURSOR, `${monsterTamerAssetPath}/ui/cursor.png`);
    this.load.image(UI_ASSET_KEYS.CURSOR_WHITE, `${monsterTamerAssetPath}/ui/cursor_white.png`);
    this.load.image(UI_ASSET_KEYS.MENU_BACKGROUND, `${kenneysAssetPath}/ui-space-expansion/glassPanel.png`);
    this.load.image(
      UI_ASSET_KEYS.MENU_BACKGROUND_PURPLE,
      `${kenneysAssetPath}/ui-space-expansion/glassPanel_purple.png`
    );
    this.load.image(UI_ASSET_KEYS.MENU_BACKGROUND_GREEN, `${kenneysAssetPath}/ui-space-expansion/glassPanel_green.png`);
    this.load.image(UI_ASSET_KEYS.BLUE_BUTTON, `${kenneysAssetPath}/ui-pack/blue_button01.png`);
    this.load.image(UI_ASSET_KEYS.BLUE_BUTTON_SELECTED, `${kenneysAssetPath}/ui-pack/blue_button00.png`);

    // Cache-buster for ALL data/map files — their names are fixed but content changes when maps,
    // NPCs, signs, monsters, or attacks are edited. Bump this whenever any assets/data file changes
    // so returning players refetch instead of using a stale cached copy. (Previously only a couple of
    // loads had it → edited maps/NPCs/healer stayed invisible for cached players.)
    const MAPV = '?v=46';

    // load json data
    this.load.json(DATA_ASSET_KEYS.ATTACKS, `assets/data/attacks.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.ANIMATIONS, `assets/data/animations.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.ITEMS, `assets/data/items.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.MONSTERS, `assets/data/monsters.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.ENCOUNTERS, `assets/data/encounters.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.NPCS, `assets/data/npcs.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.EVENTS, `assets/data/events.json${MAPV}`);
    this.load.json(DATA_ASSET_KEYS.SIGNS, `assets/data/signs.json${MAPV}`);

    // load custom fonts
    this.load.addFile(new WebFontFileLoader(this.load, [KENNEY_FUTURE_NARROW_FONT_NAME]));

    // load attack assets
    this.load.spritesheet(ATTACK_ASSET_KEYS.ICE_SHARD, `${pimenAssetPath}/ice-attack/active.png`, {
      frameWidth: 32,
      frameHeight: 32,
    });
    this.load.spritesheet(ATTACK_ASSET_KEYS.ICE_SHARD_START, `${pimenAssetPath}/ice-attack/start.png`, {
      frameWidth: 32,
      frameHeight: 32,
    });
    this.load.spritesheet(ATTACK_ASSET_KEYS.SLASH, `${pimenAssetPath}/slash.png`, {
      frameWidth: 48,
      frameHeight: 48,
    });

    // load world assets
    this.load.spritesheet(WORLD_ASSET_KEYS.GRASS, `${monsterTamerAssetPath}/map/bushes.png`, {
      frameWidth: 64,
      frameHeight: 64,
    });
    this.load.image(WORLD_ASSET_KEYS.MAIN_1_BACKGROUND, `${monsterTamerAssetPath}/map/main_1_level_background.png`);
    this.load.tilemapTiledJSON(WORLD_ASSET_KEYS.MAIN_1_LEVEL, `assets/data/main_1.json${MAPV}`);
    this.load.image(WORLD_ASSET_KEYS.WORLD_COLLISION, `${monsterTamerAssetPath}/map/collision.png`);
    this.load.image(WORLD_ASSET_KEYS.MAIN_1_FOREGROUND, `${monsterTamerAssetPath}/map/main_1_level_foreground.png`);
    this.load.image(WORLD_ASSET_KEYS.WORLD_ENCOUNTER_ZONE, `${monsterTamerAssetPath}/map/encounter.png`);
    this.load.spritesheet(WORLD_ASSET_KEYS.BEACH, `${axulArtAssetPath}/beach/crushed.png`, {
      frameWidth: 64,
      frameHeight: 64,
    });
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_1_FOREGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_1_level_foreground.png`
    );
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_1_BACKGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_1_level_background.png`
    );
    this.load.tilemapTiledJSON(BUILDING_ASSET_KEYS.BUILDING_1_LEVEL, `assets/data/building_1.json${MAPV}`);
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_2_FOREGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_2_level_foreground.png`
    );
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_2_BACKGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_2_level_background.png`
    );
    this.load.tilemapTiledJSON(BUILDING_ASSET_KEYS.BUILDING_3_LEVEL, `assets/data/building_3.json${MAPV}`);
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_3_FOREGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_3_level_foreground.png`
    );
    this.load.image(
      BUILDING_ASSET_KEYS.BUILDING_3_BACKGROUND,
      `${monsterTamerAssetPath}/map/buildings/building_3_level_background.png`
    );
    this.load.tilemapTiledJSON(BUILDING_ASSET_KEYS.BUILDING_2_LEVEL, `assets/data/building_2.json${MAPV}`);

    this.load.image(WORLD_ASSET_KEYS.FOREST_1_BACKGROUND, `${monsterTamerAssetPath}/map/forest_1_level_background.png`);
    this.load.image(WORLD_ASSET_KEYS.FOREST_1_FOREGROUND, `${monsterTamerAssetPath}/map/forest_1_level_foreground.png`);
    this.load.tilemapTiledJSON(WORLD_ASSET_KEYS.FOREST_1_LEVEL, `assets/data/forest_1.json${MAPV}`);

    // generated open-world overworld + cave/tunnel (see scripts/gen-world.mjs)
    // (MAPV cache-buster is defined once near the top of preload, above the json loads.)
    this.load.image(
      WORLD_ASSET_KEYS.OVERWORLD_1_BACKGROUND,
      // WEBP (lossy q82): the painted overworld ground was a 6144² PNG = 38 MB, the single biggest
      // cause of the very-long load / black-screen-on-slow-connection. WEBP cuts it to ~7 MB, same dims.
      `${monsterTamerAssetPath}/map/overworld_1_level_background.webp${MAPV}`
    );
    this.load.image(
      WORLD_ASSET_KEYS.OVERWORLD_1_FOREGROUND,
      `${monsterTamerAssetPath}/map/overworld_1_level_foreground.png${MAPV}`
    );
    this.load.tilemapTiledJSON(WORLD_ASSET_KEYS.OVERWORLD_1_LEVEL, `assets/data/overworld_1.json${MAPV}`);
    this.load.image(WORLD_ASSET_KEYS.CAVE_1_BACKGROUND, `${monsterTamerAssetPath}/map/cave_1_level_background.png${MAPV}`);
    this.load.image(WORLD_ASSET_KEYS.CAVE_1_FOREGROUND, `${monsterTamerAssetPath}/map/cave_1_level_foreground.png${MAPV}`);
    this.load.tilemapTiledJSON(WORLD_ASSET_KEYS.CAVE_1_LEVEL, `assets/data/cave_1.json${MAPV}`);

    // load character images
    this.load.spritesheet(CHARACTER_ASSET_KEYS.PLAYER, `${axulArtAssetPath}/character/custom.png`, {
      frameWidth: 64,
      frameHeight: 88,
    });
    this.load.spritesheet(CHARACTER_ASSET_KEYS.NPC, `${pbGamesAssetPath}/characters.png`, {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Overworld character skins (MPWSP01 "ow*" walk sheets). Each is a 128x128, 4col x 4row
    // grid of 32px frames (rows: DOWN / LEFT / RIGHT / UP, 4 walk frames each). The player and
    // NPCs draw from these so they are no longer a single reused skin. Animations are generated
    // in #createSkinAnimations() below.
    PLAYER_SKIN_KEYS.forEach((skinKey, i) => {
      this.load.spritesheet(skinKey, `${mpwspAssetPath}/character-overworld/ow${i + 1}.png`, {
        frameWidth: 32,
        frameHeight: 32,
      });
    });

    // ui components for title
    this.load.image(TITLE_ASSET_KEYS.BACKGROUND, `${monsterTamerAssetPath}/ui/title/background.png`);
    this.load.image(TITLE_ASSET_KEYS.PANEL, `${monsterTamerAssetPath}/ui/title/title_background.png`);
    this.load.image(TITLE_ASSET_KEYS.TITLE, `${monsterTamerAssetPath}/ui/title/title_text.png`);

    // ui components for monster party
    this.load.image(
      MONSTER_PARTY_ASSET_KEYS.PARTY_BACKGROUND,
      `${monsterTamerAssetPath}/ui/monster-party/background.png`
    );
    this.load.image(
      MONSTER_PARTY_ASSET_KEYS.MONSTER_DETAILS_BACKGROUND,
      `${monsterTamerAssetPath}/ui/monster-party/monster-details-background.png`
    );

    // ui components for inventory
    this.load.image(
      INVENTORY_ASSET_KEYS.INVENTORY_BACKGROUND,
      `${monsterTamerAssetPath}/ui/inventory/bag_background.png`
    );
    this.load.image(INVENTORY_ASSET_KEYS.INVENTORY_BAG, `${monsterTamerAssetPath}/ui/inventory/bag.png`);

    // load audio
    this.load.setPath('assets/audio/xDeviruchi');
    // OGG instead of the original ~20 MB-each WAVs (uncompressed) → ~1.5 MB each. Big startup win.
    this.load.audio(AUDIO_ASSET_KEYS.BATTLE, 'Decisive-Battle.ogg');
    this.load.audio(AUDIO_ASSET_KEYS.TITLE, 'Title-Theme.ogg');
    this.load.setPath('assets/audio/leohpaz');
    this.load.audio(AUDIO_ASSET_KEYS.CLAW, '03_Claw_03.wav');
    this.load.audio(AUDIO_ASSET_KEYS.GRASS, '03_Step_grass_03.wav');
    this.load.audio(AUDIO_ASSET_KEYS.ICE, '13_Ice_explosion_01.wav');
    this.load.audio(AUDIO_ASSET_KEYS.FLEE, '51_Flee_02.wav');
    // Footsteps + new overworld music loop + defeat voice + coin pickup (root assets/audio dir).
    this.load.setPath('assets/audio');
    this.load.audio(AUDIO_ASSET_KEYS.STEP_SAND, 'step_sand.mp3');
    this.load.audio(AUDIO_ASSET_KEYS.STEP_GRASS, 'step_grass.mp3');
    this.load.audio(AUDIO_ASSET_KEYS.MAIN, 'music_overworld.mp3'); // replaces the old overworld theme
    this.load.audio(AUDIO_ASSET_KEYS.GAME_OVER, 'gameover.mp3');
    this.load.audio(AUDIO_ASSET_KEYS.COIN, 'coin.mp3');
  }

  /**
   * @returns {void}
   */
  create() {
    super.create();

    // create animations from json file
    this.#createAnimations();
    // create the per-skin overworld walk animations (player + NPC skins)
    this.#createSkinAnimations();

    // attempt to populate data manager with saved data and initialize
    dataManager.loadData();
    // set global audio based on data manager settings
    setGlobalSoundSettings(this);

    this.scene.start(SCENE_KEYS.ENTRY_SCENE);
  }

  /**
   * @returns {void}
   */
  /**
   * Generates the 4 directional walk animations for every overworld character skin
   * (MPWSP "ow*" sheets). Each sheet is a 4col x 4row 32px grid where the rows are
   * DOWN / LEFT / RIGHT / UP and each row's 4 columns are walk frames. We build a 3-frame
   * yoyo cycle `[col1, col0, col2]` per direction so the middle frame (index 1) is the
   * neutral standing pose — Character.update() reads `frames[1]` as the idle frame.
   *
   * Animation keys are `SKIN_<skinKey>_<DIRECTION>` (e.g. `SKIN_PLAYER_SKIN_1_DOWN`).
   * @returns {void}
   */
  #createSkinAnimations() {
    // row index per facing direction in the MPWSP "ow*" sheet layout
    const ROW = { DOWN: 0, LEFT: 1, RIGHT: 2, UP: 3 };
    PLAYER_SKIN_KEYS.forEach((skinKey) => {
      Object.entries(ROW).forEach(([direction, row]) => {
        const base = row * 4;
        const key = `SKIN_${skinKey}_${direction}`;
        if (this.anims.exists(key)) {
          return;
        }
        this.anims.create({
          key,
          frames: this.anims.generateFrameNumbers(skinKey, { frames: [base + 1, base, base + 2] }),
          frameRate: 6,
          repeat: -1,
          yoyo: true,
        });
      });
    });
  }

  /**
   * @returns {void}
   */
  #createAnimations() {
    const animations = DataUtils.getAnimations(this);
    animations.forEach((animation) => {
      const frames = animation.frames
        ? this.anims.generateFrameNumbers(animation.assetKey, { frames: animation.frames })
        : this.anims.generateFrameNumbers(animation.assetKey);
      this.anims.create({
        key: animation.key,
        frames: frames,
        frameRate: animation.frameRate,
        repeat: animation.repeat,
        delay: animation.delay,
        yoyo: animation.yoyo,
      });
    });
  }
}
